/**
 * A searchable dropdown for the scope bar.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Search matches the NAME and the ID, because campaigns are routinely known by
 * id ("1", a Voluum UUID) and tracker names repeat across countries. Three
 * filters compose: the search term, the tracker pills, and "with overrides" —
 * the last being how you find the handful of configured entries among ~250.
 */

import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import type { TrackerId } from '@trafficpulse/shared';

import { useModalPortalSurface, type ProviderFilterValue } from '@/components/ui';
import { cn } from '@/lib/utils';

import { ScopeComboboxHeader } from './ScopeComboboxHeader';
import { ScopeComboboxTrigger } from './ScopeComboboxTrigger';
import { ScopeOptionRow } from './ScopeOptionRow';
import type { ScopeOption } from './types';

const MAX_VISIBLE = 200;

export const ScopeCombobox: FC<{
  label: string;
  value: string;
  placeholder: string;
  options: ScopeOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Show the All + enabled tracker pills — campaigns only. */
  showProviderFilter?: boolean;
  /** Extra room for rows carrying a badge and an id alongside the name. */
  wide?: boolean;
}> = ({
  label,
  value,
  placeholder,
  options,
  onChange,
  disabled = false,
  showProviderFilter = false,
  wide = false,
}) => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [provider, setProvider] = useState<ProviderFilterValue<TrackerId>>('all');
  const [onlyOverridden, setOnlyOverridden] = useState(false);
  const portalSurface = useModalPortalSurface();
  const inputRef = useRef<HTMLInputElement>(null);

  // Reopening starts clean, so a stale filter can never make the list look empty.
  useEffect(() => {
    if (!open) {
      setTerm('');
      setOnlyOverridden(false);
    }
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const overriddenTotal = useMemo(
    () => options.filter((o) => (o.count ?? 0) > 0).length,
    [options],
  );

  const q = term.trim().toLowerCase();
  const visible = useMemo(() => {
    let rows = options;
    if (provider !== 'all') rows = rows.filter((o) => o.provider === provider);
    if (onlyOverridden) rows = rows.filter((o) => (o.count ?? 0) > 0);
    if (q) rows = rows.filter((o) => o.search.includes(q));
    return rows.slice(0, MAX_VISIBLE);
  }, [options, provider, onlyOverridden, q]);

  const pick = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <div className="flex min-w-0 items-center gap-1.5 text-xs">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild disabled={disabled}>
          <ScopeComboboxTrigger
            label={label}
            placeholder={placeholder}
            selected={selected}
            onClear={() => onChange('')}
          />
        </Popover.Trigger>
        <Popover.Portal container={portalSurface}>
          <Popover.Content
            align="start"
            sideOffset={4}
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              inputRef.current?.focus();
            }}
            className={cn(
              'z-[100] rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-lg',
              // Wide only where a Voluum UUID competes with the name for room.
              wide ? 'w-[min(34rem,calc(100vw-2rem))]' : 'w-[min(18rem,calc(100vw-2rem))]',
            )}
          >
            <ScopeComboboxHeader
              label={label}
              term={term}
              onTerm={setTerm}
              inputRef={inputRef}
              overriddenTotal={overriddenTotal}
              onlyOverridden={onlyOverridden}
              onOnlyOverridden={setOnlyOverridden}
              showProviderFilter={showProviderFilter}
              provider={provider}
              onProvider={setProvider}
            />
            <div className="max-h-72 overflow-y-auto py-1">
              {!onlyOverridden && (
                <button
                  type="button"
                  onClick={() => pick('')}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-muted"
                >
                  {placeholder}
                </button>
              )}
              {visible.length === 0 ? (
                <p className="px-2 py-3 text-center text-xs text-muted-foreground">
                  {q ? `Nothing matches “${term}”` : 'Nothing to show'}
                </p>
              ) : (
                visible.map((o) => (
                  <ScopeOptionRow
                    key={o.value}
                    option={o}
                    selected={o.value === value}
                    onPick={pick}
                  />
                ))
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};
